import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { productAPI } from '../services/api';
import { useLang } from '../context/LangContext';
import ProductCard from '../components/shop/ProductCard';
import './Home.css';

const CATEGORIES = [
  { val:'pooja-samagri', icon:'🪔', hi:'पूजा सामग्री', en:'Pooja Samagri' },
  { val:'idols-murti', icon:'🛕', hi:'मूर्तियाँ', en:'Idols & Murti' },
  { val:'rudraksha-yantra', icon:'📿', hi:'रुद्राक्ष व यंत्र', en:'Rudraksha & Yantra' },
  { val:'religious-clothing', icon:'👘', hi:'धार्मिक वस्त्र', en:'Religious Clothing' },
  { val:'temple-essentials', icon:'🔔', hi:'मंदिर सामग्री', en:'Temple Essentials' },
  { val:'festival-special', icon:'🎉', hi:'त्योहार कलेक्शन', en:'Festival Special' },
];

const TESTIMONIALS = [
  { name:'Rajesh Sharma', city:'Ayodhya', hi:'बहुत अच्छी पूजा सामग्री। श्री राम मंदिर के लिए मूर्ति यहीं से ली।', en:'Excellent pooja samagri. Got the murti for Ram Temple from here.', rating:5 },
  { name:'Priya Gupta', city:'Delhi', hi:'रुद्राक्ष माला प्रमाणित है। बहुत सुंदर पैकेजिंग।', en:'Certified Rudraksha mala. Beautiful packaging!', rating:5 },
  { name:'Ankit Mishra', city:'USA (NRI)', hi:'अमेरिका में भी Ayodhya की सामग्री मिल गई!', en:'Got Ayodhya products in USA — amazing NRI service!', rating:5 },
];

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    productAPI.getAll({ featured: 'true', limit: 8, sort: '-createdAt' })
      .then(res => setFeatured(res.products || []))
      .catch(() => {});
  }, []);

  return (
    <div className="home-page">
      {/* HERO */}
      <section className="hero-section">
        <div className="hero-bg-pattern" />
        <div className="container hero-content">
          <div className="hero-badge font-display">✨ AYODHYA AUTHENTIC</div>
          <h1 className="hero-title font-devanagari">
            जय श्री राम 🙏<br />
            <span className="hero-title-sub">राघवेंद्र पूजन भंडार</span>
          </h1>
          <p className="hero-desc">{t({ hi:'अयोध्या से प्रामाणिक पूजा सामग्री, मूर्तियाँ, रुद्राक्ष और वैदिक ज्योतिष सेवाएं', en:'Authentic Pooja Samagri, Idols, Rudraksha & Vedic Astrology from Ayodhya' })}</p>
          <div className="hero-cta-row">
            <Link to="/shop" className="btn btn-primary hero-cta">🛍️ {t({ hi:'दुकान देखें', en:'Explore Shop' })}</Link>
            <Link to="/astrology" className="btn btn-outline-light hero-cta">🔮 {t({ hi:'ज्योतिष', en:'Astrology' })}</Link>
          </div>
          <div className="hero-trust-bar">
            {['🚚 Free Shipping ₹499+','🔒 Razorpay Secure','✅ GST Invoice','🌍 NRI Delivery','🛕 Temple Certified'].map(f => <span key={f} className="hero-trust-item">{f}</span>)}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t({ hi:'श्रेणियाँ', en:'Categories' })}</h2>
            <div className="section-divider" />
          </div>
          <div className="categories-grid">
            {CATEGORIES.map(c => (
              <Link key={c.val} to={`/shop?category=${c.val}`} className="category-card">
                <span className="cat-icon">{c.icon}</span>
                <span className="cat-name font-devanagari">{t(c)}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t({ hi:'विशेष उत्पाद', en:'Featured Products' })}</h2>
            <p className="section-subtitle">{t({ hi:'अयोध्या से सर्वश्रेष्ठ चयन', en:'Best picks from Ayodhya' })}</p>
            <div className="section-divider" />
          </div>
          {featured.length === 0 ? <div className="spinner" /> : (
            <div className="grid-auto">{featured.map(p => <ProductCard key={p._id} product={p} />)}</div>
          )}
          <div style={{ textAlign:'center', marginTop:'2rem' }}>
            <Link to="/shop" className="btn btn-outline">🛕 {t({ hi:'सभी उत्पाद देखें', en:'View All Products' })}</Link>
          </div>
        </div>
      </section>

      {/* ASTROLOGY PROMO */}
      <section className="astro-promo-section">
        <div className="astro-promo-stars" />
        <div className="container astro-promo-content">
          <div>
            <h2 className="astro-promo-title font-devanagari">🔮 वैदिक ज्योतिष सेवाएं</h2>
            <p className="astro-promo-sub">Vedic Astrology · कुंडली · राशिफल · पंचांग</p>
            <p className="astro-promo-disclaimer">⚠️ {t({ hi:'ये सेवाएं केवल मार्गदर्शन हेतु हैं।', en:'These services are for guidance only.' })}</p>
          </div>
          <div className="astro-services-mini">
            {[{icon:'📜',hi:'कुंडली',en:'Kundali',price:'₹299'},{icon:'💍',hi:'कुंडली मिलान',en:'Kundali Milan',price:'₹499'},{icon:'⏰',hi:'मुहूर्त',en:'Muhurat',price:'₹399'},{icon:'📊',hi:'PDF रिपोर्ट',en:'PDF Report',price:'₹1499'}].map(s => (
              <div key={s.en} className="astro-mini-card" onClick={() => navigate('/astrology')}>
                <span className="astro-mini-icon">{s.icon}</span>
                <span className="astro-mini-name font-devanagari">{t(s)}</span>
                <span className="astro-mini-price">{s.price}</span>
              </div>
            ))}
          </div>
          <Link to="/astrology" className="btn btn-gold">✨ {t({ hi:'ज्योतिष सेवाएं देखें', en:'Explore Astrology' })}</Link>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t({ hi:'ग्राहक समीक्षाएं', en:'Customer Reviews' })}</h2>
            <div className="section-divider" />
          </div>
          <div className="testimonials-grid">
            {TESTIMONIALS.map((r, i) => (
              <div key={i} className="testimonial-card">
                <div className="testimonial-stars">{'★'.repeat(r.rating)}</div>
                <p className="testimonial-text font-devanagari">{t(r)}</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">{r.name[0]}</div>
                  <div><div className="testimonial-name">{r.name}</div><div className="testimonial-city">{r.city}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section why-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t({ hi:'हमें क्यों चुनें?', en:'Why Choose Us?' })}</h2>
            <div className="section-divider" />
          </div>
          <div className="why-grid">
            {[
              {icon:'🛕',hi:'अयोध्या प्रमाणित',en:'Ayodhya Certified',desc:{hi:'सीधे अयोध्या के मंदिरों और कारीगरों से',en:'Directly from Ayodhya temples and artisans'}},
              {icon:'🚚',hi:'तेज़ शिपिंग',en:'Fast Shipping',desc:{hi:'Shiprocket द्वारा 29,000+ पिन कोड',en:'29,000+ pincodes via Shiprocket'}},
              {icon:'💳',hi:'सुरक्षित भुगतान',en:'Secure Payment',desc:{hi:'Razorpay PCI DSS · UPI · Card · COD',en:'Razorpay PCI DSS · UPI · Card · COD'}},
              {icon:'📄',hi:'GST बिल',en:'GST Invoice',desc:{hi:'हर ऑर्डर पर डिजिटल GST इनवॉइस',en:'Digital GST invoice with every order'}},
              {icon:'🌍',hi:'NRI डिलीवरी',en:'NRI Delivery',desc:{hi:'USA, UK, Canada, UAE और अन्य देशों में',en:'USA, UK, Canada, UAE & more'}},
              {icon:'🙏',hi:'पंडित सेवा',en:'Pandit Service',desc:{hi:'ऑनलाइन और ऑफलाइन पूजा बुकिंग',en:'Online & offline puja booking'}},
            ].map((w, i) => (
              <div key={i} className="why-card">
                <span className="why-icon">{w.icon}</span>
                <h4 className="why-title font-devanagari">{t(w)}</h4>
                <p className="why-desc">{t(w.desc)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
